import {
  AutomatedEmailCampaignType,
  IAutomatedEmailCampaignSetting,
  IEmailReplyPrompt,
} from 'wisestep-api';
import * as yup from 'yup';
export const promptSchema = yup.object({
  promptTitle: yup.string().trim().required('Prompt title is required'),
  prompt: yup.string().trim().required('Prompt is required'),
});

export const defaultValidationSchema = yup.object({
  id: yup.string(),
  campaignStatus: yup.boolean(),
  emailCheck: yup.boolean(),
  totalEmails: yup
    .number()
    .min(1, 'Minimum 1 email required')
    .max(1000, 'Maximum 1000 emails allowed')
    .typeError('Please enter the totalNumber of emails for campaign')
    .required('Please enter the totalNumber of emails for campaign'),
  aiReplyEnabled: yup.boolean(),
  emailReplyPrompts: yup.array().of(
    promptSchema.shape({
      isActive: yup.boolean(),
    })
  ),
  manualReply: yup.boolean(),
  manualEmail: yup.mixed().when('manualReply', {
    is: true,
    then: () =>
      yup
        .object({
          email: yup
            .string()
            .email('Invalid email format')
            .required('Email is required when Manual Reply is enabled'),
        })
        .required('Manual email is required')
        .typeError('Manual email is required'),
    otherwise: () => yup.mixed().notRequired().nullable(),
  }),
  callBackRequest: yup.boolean(),
  callbackEmail: yup.mixed().when('callBackRequest', {
    is: true,
    then: () =>
      yup
        .object({
          email: yup
            .string()
            .email('Invalid email format')
            .required('Email is required when Manual Reply is enabled'),
        })
        .required('Callback email is required')
        .typeError('Callback email is required'),
    otherwise: () => yup.mixed().notRequired().nullable(),
  }),
  subject: yup.string().trim().required('Please fill the email template subject'),
  emailBody: yup.string().trim().required('Please fill the email template body'),
  bcc: yup.array().of(yup.string().email('Invalid email in BCC')).notRequired(),
  cc: yup.array().of(yup.string().email('Invalid email in CC')).notRequired(),
});

export const clientSpocValidationSchema = defaultValidationSchema.shape({
  clientContact: yup
    .object({ id: yup.string().required('Client SPOC is required') })
    .typeError('Please select a client SPOC')
    .required('Please select a client SPOC'),
});

export const clientValidationSchema = defaultValidationSchema.shape({
  client: yup
    .object({ value: yup.string().required('Client is required') })
    .typeError('Please select a valid client')
    .required('Please select the client required'),
});

export const jobValidationSchema = defaultValidationSchema.shape({
  job: yup
    .object({ id: yup.string().required('Job is required') })
    .typeError('Please select a valid job')
    .required('Please select the Job required'),
});

export const sourceValidationSchema = defaultValidationSchema.shape({
  source: yup
    .string()
    .trim()
    .required('Please select the source')
    .typeError('Please select the Source'),
});

export const defaultValues = {
  id: '',
  emailCheck: false,
  campaignStatus: false,
  totalEmails: 100,
  aiReplyEnabled: true,
  manualReply: false,
  manualEmail: null,
  callBackRequest: false,
  callbackEmail: null,
  emailReplyPrompts: [] as IEmailReplyPrompt[],
  bcc: [],
  cc: [],
  subject: '',
  emailBody: '',
  preferredAccountId: '',
  fromEmail: '',
};

export const getCampaignDefaultValues = (
  response: IAutomatedEmailCampaignSetting,
  type: AutomatedEmailCampaignType
) => {
  if (!response) return defaultValues;

  const base = {
    client: null,
    clientContact: null,
    job: null,
    manualEmail: response.manualReplyFrom ? { email: response.manualReplyFrom } : null,
    callbackEmail: response.callBackRequestFrom ? { email: response.callBackRequestFrom } : null,
    bcc: [],
    cc: [],
    ...response,
  };

  const typeMap: Record<AutomatedEmailCampaignType, Partial<typeof base>> = {
    [AutomatedEmailCampaignType.CLIENT]: { client: response.metaDetails },
    [AutomatedEmailCampaignType.CLIENT_SPOC]: { clientContact: response.metaDetails },
    [AutomatedEmailCampaignType.JOB]: { job: response.metaDetails },
    [AutomatedEmailCampaignType.SOURCE]: {},
    [AutomatedEmailCampaignType.DEFAULT]: {},
  };

  return {
    ...base,
    ...typeMap[type],
  };
};

export type JobFormValues = yup.InferType<typeof jobValidationSchema>;
export type ClientSpocFormValues = yup.InferType<typeof clientSpocValidationSchema>;
export type SourceFormValues = yup.InferType<typeof sourceValidationSchema>;
export type DefaultFormValues = yup.InferType<typeof defaultValidationSchema>;
export type ClientFormValues = yup.InferType<typeof clientValidationSchema>;

interface SchemaAndDefaults<TSchema extends yup.ObjectSchema<any>> {
  schema: TSchema;
  defaultValues: yup.InferType<TSchema>;
}

export const defaultSchemaAndValues: Record<
  AutomatedEmailCampaignType,
  SchemaAndDefaults<yup.ObjectSchema<any>>
> = {
  [AutomatedEmailCampaignType.JOB]: {
    schema: jobValidationSchema,
    defaultValues: {
      ...defaultValues,
      job: null,
    },
  },
  [AutomatedEmailCampaignType.CLIENT_SPOC]: {
    schema: clientSpocValidationSchema,
    defaultValues: {
      ...defaultValues,
      clientContact: null,
    },
  },
  [AutomatedEmailCampaignType.SOURCE]: {
    schema: sourceValidationSchema,
    defaultValues: {
      ...defaultValues,
      source: '',
    },
  },
  [AutomatedEmailCampaignType.DEFAULT]: {
    schema: defaultValidationSchema,
    defaultValues: defaultValues,
  },
  [AutomatedEmailCampaignType.CLIENT]: {
    schema: clientValidationSchema,
    defaultValues: {
      ...defaultValues,
      client: null,
    },
  },
};
